import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Search, Edit2, Trash2, X, Save, ClipboardList } from 'lucide-react';

export default function TaskCatalogPage() {
  const { api } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [meta, setMeta] = useState({ categories: [], contractor_types: [], units: [] });
  const [contractors, setContractors] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', category: '', contractor_type: '', default_unit: 'each', default_rate: 0, default_contractor_id: '', default_contractor_name: '', description: '', active: true });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: m }, { data: c }] = await Promise.all([
        api.get(`/task-catalog?search=${search}&category=${filterCat}`),
        api.get('/task-catalog/meta'),
        api.get('/contractors'),
      ]);
      setTasks(t);
      setMeta(m);
      setContractors(c);
    } catch { /* ignore */ }
    setLoading(false);
  }, [api, search, filterCat]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (editId) await api.put(`/task-catalog/${editId}`, form);
    else await api.post('/task-catalog', form);
    setShowForm(false); setEditId(null);
    setForm({ name: '', category: '', contractor_type: '', default_unit: 'each', default_rate: 0, description: '', active: true });
    load();
  };

  const handleEdit = (t) => {
    setForm({ name: t.name, category: t.category, contractor_type: t.contractor_type, default_unit: t.default_unit, default_rate: t.default_rate, default_contractor_id: t.default_contractor_id || '', default_contractor_name: t.default_contractor_name || '', description: t.description || '', active: t.active !== false });
    setEditId(t._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await api.delete(`/task-catalog/${id}`);
    load();
  };

  // Group by category
  const grouped = tasks.reduce((acc, t) => {
    const cat = t.category || 'Uncategorised';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div data-testid="task-catalog-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Task Catalog</h1>
          <p className="text-sm text-slate-500 mt-1">Reusable task library for job planning. {tasks.length} tasks defined.</p>
        </div>
        <button data-testid="add-task-btn" onClick={() => { setShowForm(true); setEditId(null); setForm({ name: '', category: '', contractor_type: '', default_unit: 'each', default_rate: 0, default_contractor_id: '', default_contractor_name: '', description: '', active: true }); }} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] transition-colors">
          <Plus size={14} /> Add Task
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="task-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]" />
        </div>
        <select data-testid="task-category-filter" value={filterCat} onChange={e => setFilterCat(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]">
          <option value="">All Categories</option>
          {meta.categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div data-testid="task-form" className="bg-white border-2 border-[#D35400] rounded-xl p-5">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-3">{editId ? 'Edit Task' : 'New Task'}</h2>
          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Task Name *</label>
              <input data-testid="task-name-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g. Strip existing roof" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
              <select data-testid="task-category-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="">Select...</option>
                {meta.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contractor Type</label>
              <select data-testid="task-contractor-type" value={form.contractor_type} onChange={e => setForm({ ...form, contractor_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="">Select...</option>
                {meta.contractor_types.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Default Unit</label>
              <select value={form.default_unit} onChange={e => setForm({ ...form, default_unit: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                {meta.units.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Default Rate ($)</label>
              <input type="number" step="0.01" value={form.default_rate} onChange={e => setForm({ ...form, default_rate: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Optional notes" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Default Contractor</label>
              <select value={form.default_contractor_id} onChange={e => { const c = contractors.find(x => x._id === e.target.value); setForm({ ...form, default_contractor_id: e.target.value, default_contractor_name: c ? c.contractor_name : '' }); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="">None</option>
                {contractors.map(c => <option key={c._id} value={c._id}>{c.contractor_name}{c.is_owner ? ' (Owner)' : ''}</option>)}
              </select>
            </div>
            <div className="md:col-span-3 flex gap-2">
              <button data-testid="save-task-btn" type="submit" className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000]"><Save size={14} /> {editId ? 'Update' : 'Create'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50"><X size={14} className="inline mr-1" />Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Task List grouped by category */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <ClipboardList size={40} className="mx-auto text-slate-300 mb-3" />
          <h3 className="text-sm font-semibold text-slate-700 mb-1">No Tasks Defined</h3>
          <p className="text-xs text-slate-500">Add tasks to build your reusable job planning library.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, catTasks]) => (
          <div key={cat} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-[#0F172A]">{cat} <span className="text-slate-400 font-normal">({catTasks.length})</span></h3>
            </div>
            <div className="divide-y divide-slate-100">
              {catTasks.map(t => (
                <div key={t._id} data-testid={`task-row-${t._id}`} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0F172A]">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.contractor_type || 'Any'} &middot; ${t.default_rate?.toFixed(2) || '0.00'}/{t.default_unit || 'each'}{t.default_contractor_name ? ` · Default: ${t.default_contractor_name}` : ''}{t.description ? ` — ${t.description}` : ''}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-task-${t._id}`} onClick={() => handleEdit(t)} className="p-1.5 text-slate-400 hover:text-[#D35400] hover:bg-orange-50 rounded transition-colors"><Edit2 size={14} /></button>
                    <button data-testid={`delete-task-${t._id}`} onClick={() => handleDelete(t._id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
